import type {
  AccountSearchRelyingPartyCount,
  AccountSearchScope,
  QuickAccountLookupResponse,
  QuickPatientSearchResult
} from '@/shared/api/graphql/account';
import { QUICK_ACCOUNT_LOOKUP } from '@/shared/api/graphql/account';
import useDebounce from '@/shared/lib/hooks/useDebounce';
import { formatPhoneNumber } from '@/shared/lib/util/format';
import type { QuestionIdeaGroup } from '@/shared/lib/util/questionIdeas';
import { looksLikePrompt } from '@/shared/lib/util/smartSearch';
import TruentityTextField from '@/shared/ui/TruentityTextField';
import { Body2, Small } from '@/shared/ui/components/Typography';
import { useLazyQuery } from '@apollo/client';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BusinessIcon from '@mui/icons-material/Business';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Chip, CircularProgress, ClickAwayListener, Divider, Paper, Popper, Stack, Tooltip } from '@mui/material';
import moment from 'moment';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Below this a term matches too much to be worth searching for. */
const MIN_TERM_LENGTH = 2;
const DEBOUNCE_MS = 250;
const RESULT_LIMIT = 8;

/** Company chips past this collapse into a "+N more" label. */
const MAX_VISIBLE_CHIPS = 3;
const CURRENT_RELYING_PARTY: AccountSearchScope = 'CURRENT_RELYING_PARTY';
const ALL_RELYING_PARTIES: AccountSearchScope = 'ALL_RELYING_PARTIES';

/**
 * Accepted formats, shown when a search finds nothing.
 */
const SEARCH_HELP_TEXT =
  'Supports a first name, a last name, or both - including part of a name and common misspellings. ' +
  'A phone number can be the whole number, the digits it starts with, or the last four. ' +
  'A Truentity ID or EHR ID can be just the first few characters.';

const PHONE_FORMATS = '5551234567 · 555 123 4567 · 555-123-4567 · (555) 123-4567 · +15551234567';
const BIRTH_DATE_FORMATS = '03/13/1978 · 13/03/1978 · 03-13-1978 · 1978-03-13';

const SEARCH_HELP = (
  <Stack spacing={0.75}>
    <span>{SEARCH_HELP_TEXT}</span>
    <span>Phone: {PHONE_FORMATS}</span>
    <span>Date of birth: {BIRTH_DATE_FORMATS}</span>
  </Stack>
);

const MATCH_NAME = 'Name';
const MATCH_PHONE = 'Phone';
const MATCH_BIRTH_DATE = 'Date of birth';

export type LookupHandoff = {
  filterOption: string;
  value: string;
};

export type FieldOverride = {
  label: string;
  /** Search the typed term against this field only. */
  onSelect: (term: string) => void;
};

type Props = {
  onSelectPatient: (row: QuickPatientSearchResult) => void;
  onSeeAllResults: (handoff: LookupHandoff, scope: AccountSearchScope) => void;
  /**
   * Explicit "search this field only" escape hatch, kept from the previous search box. The
   * default path needs no field, but restricting to one is genuinely useful - an EHR id that
   * happens to look like a phone fragment, for instance.
   */
  fieldOverrides?: FieldOverride[];
  placeholder?: string;
  /** The actor's recent questions, newest first, offered when a lookup would be pointless. */
  savedPrompts?: string[];
  /** A saved prompt was clicked. Submits it rather than only filling the box. */
  onSelectPrompt?: (prompt: string) => void;
  /** Example questions for the empty box, picked fresh each time it opens. Asked like a saved prompt. */
  getQuestionIdeas?: () => QuestionIdeaGroup[];
  /** Enter on a question-shaped term. Only called once the box has decided it is one. */
  onSubmitTerm?: (term: string) => void;
  /** False when AI Search is off: every term is a patient lookup, never a question. */
  canAskQuestions?: boolean;
  showCompanyTabs?: boolean;
};

type Group = {
  heading: string;
  rows: QuickPatientSearchResult[];
};

const formatBirthDate = (value: string | null): string =>
  // Read as a plain date, not a timestamp - otherwise it shifts by a day.
  value ? moment(value, 'YYYY-MM-DD').format('MMM DD YYYY') : '';

const formatPhone = (value: string | null): string => (value ? formatPhoneNumber(value) ?? value : '');

const secondaryLine = (row: QuickPatientSearchResult): string =>
  [formatBirthDate(row.birthDate), formatPhone(row.phone), row.truentityId].filter(Boolean).join(' · ');

/**
 * Whether a term can be handed to the Patients Lookup page, and in which field.
 *
 * Only a name, phone or date of birth - the fields that page can search. An ID returns null, so
 * the dropdown shows no "See all results" and Enter does nothing.
 */
const lookupHandoffFor = (rows: QuickPatientSearchResult[], term: string): LookupHandoff | null => {
  const match = rows.find(row => [MATCH_NAME, MATCH_PHONE, MATCH_BIRTH_DATE].includes(row.matchedOn));
  if (!match) return null;

  switch (match.matchedOn) {
    case MATCH_PHONE:
      // Digits only, or that page finds nothing.
      return { filterOption: 'phoneNumber', value: term.replace(/\D/g, '') };
    case MATCH_BIRTH_DATE:
      // The matched patient's own date, so the format is always one the date field accepts.
      return { filterOption: 'dateOfBirth', value: match.birthDate ?? term };
    default:
      return { filterOption: nameFieldFor(rows, term), value: term };
  }
};

/** Which name field a single word belongs in, weighed across every name row on screen. */
const nameFieldFor = (rows: QuickPatientSearchResult[], term: string): string => {
  if (term.split(/\s+/).length > 1) return '';

  const needle = term.toLowerCase();
  const names = rows
    .filter(row => row.matchedOn === MATCH_NAME)
    .map(row => {
      const [first = '', ...rest] = (row.name ?? '').trim().toLowerCase().split(/\s+/);
      return { first, last: rest.join(' ') };
    });

  const pick = (test: (value: string) => boolean): string => {
    const last = names.filter(name => test(name.last)).length;
    const first = names.filter(name => test(name.first)).length;
    if (last === 0 && first === 0) return '';

    return first > last ? 'firstName' : 'lastName';
  };

  return pick(value => value.startsWith(needle)) || pick(value => value.includes(needle));
};

/** The patient id alone is not unique: someone in two pharmacies appears once per pharmacy. */
const rowKey = (row: QuickPatientSearchResult): string => `${row.truentityId}:${row.relyingPartyId ?? ''}`;

/** "100+" once the server stopped counting, so the number never reads as exact when it is not. */
const formatCount = (count: number, isCapped: boolean): string => `${count}${isCapped ? '+' : ''}`;

/** Keeps a company's rows together inside a group. */
const byCompany = (a: QuickPatientSearchResult, b: QuickPatientSearchResult): number =>
  (a.relyingPartyName ?? '').localeCompare(b.relyingPartyName ?? '');

/** Exact matches first, grouped by the field they matched, with near-misses last. */
const groupResults = (rows: QuickPatientSearchResult[]): Group[] => {
  const exact = rows.filter(row => !row.isFuzzy);
  const fuzzy = rows.filter(row => row.isFuzzy);

  const byField = exact.reduce<Record<string, QuickPatientSearchResult[]>>((groups, row) => {
    groups[row.matchedOn] = [...(groups[row.matchedOn] ?? []), row];
    return groups;
  }, {});

  const groups: Group[] = Object.entries(byField).map(([heading, groupRows]) => ({
    heading,
    rows: [...groupRows].sort(byCompany)
  }));
  if (fuzzy.length > 0) groups.push({ heading: 'Did you mean', rows: [...fuzzy].sort(byCompany) });

  return groups;
};

/**
 * The patient search box in the navbar.
 *
 * Every keystroke fetches the current pharmacy's results, plus the match counts for all of them
 * so the tabs can show a number. The other pharmacies' results are only fetched when that tab is
 * opened, and the tab then stays open as the term changes.
 */
const PatientSearchBox = ({
  onSelectPatient,
  onSeeAllResults,
  onSubmitTerm,
  fieldOverrides = [],
  placeholder,
  savedPrompts = [],
  onSelectPrompt,
  getQuestionIdeas,
  canAskQuestions = true,
  showCompanyTabs = false
}: Props): React.ReactElement => {
  const [term, setTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [highlighted, setHighlighted] = useState(-1);
  const [scope, setScope] = useState<AccountSearchScope>(CURRENT_RELYING_PARTY);
  const [ideaGroups, setIdeaGroups] = useState<QuestionIdeaGroup[]>([]);

  const [rows, setRows] = useState<QuickPatientSearchResult[]>([]);
  const [allRows, setAllRows] = useState<QuickPatientSearchResult[]>([]);
  const [counts, setCounts] = useState<AccountSearchRelyingPartyCount[]>([]);
  const [total, setTotal] = useState<{ count: number; isCapped: boolean }>({ count: 0, isCapped: false });
  // Separate from the rows: "found nothing" and "the search failed" look the same otherwise.
  const [failed, setFailed] = useState(false);
  const [allLoading, setAllLoading] = useState(false);

  const debouncedTerm = useDebounce(term, DEBOUNCE_MS);

  // Replies can arrive out of order, so a slow early one must not overwrite a newer result.
  const latestRequest = useRef(0);
  const latestAllRequest = useRef(0);
  // Which term the other-pharmacy results belong to, so reopening the tab costs nothing.
  const allRowsTerm = useRef<string | null>(null);

  const [runLookup, { loading }] = useLazyQuery<QuickAccountLookupResponse>(QUICK_ACCOUNT_LOOKUP, {
    fetchPolicy: 'no-cache'
  });
  const [runAllCompanies] = useLazyQuery<QuickAccountLookupResponse>(QUICK_ACCOUNT_LOOKUP, {
    fetchPolicy: 'no-cache'
  });

  // A question is not worth a lookup request: no patient name is four words long or over
  // 30 characters, so searching for each word of "who got critical alerts yesterday" can
  // only burn a round trip. Enter still sends it to AI Search.
  const trimmedTerm = debouncedTerm.trim();
  const isPrompt = canAskQuestions && looksLikePrompt(trimmedTerm);
  const searchable = trimmedTerm.length >= MIN_TERM_LENGTH && !isPrompt;

  const lookupFailed = (error: unknown): boolean => Boolean(error);

  useEffect(() => {
    if (!searchable) {
      setRows([]);
      setCounts([]);
      setTotal({ count: 0, isCapped: false });
      setFailed(false);
      allRowsTerm.current = null;
      setAllRows([]);
      return;
    }

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    runLookup({
      variables: { term: trimmedTerm, limit: RESULT_LIMIT, scope: CURRENT_RELYING_PARTY, includeCounts: showCompanyTabs }
    })
      .then(({ data, error }) => {
        if (latestRequest.current !== requestId) return;

        if (lookupFailed(error)) {
          setFailed(true);
          setRows([]);
          setCounts([]);
          return;
        }

        const lookup = data?.quickAccountLookup;
        setFailed(false);
        setRows(lookup?.results ?? []);
        setCounts(lookup?.relyingPartyCounts ?? []);
        setTotal({ count: lookup?.totalCount ?? 0, isCapped: Boolean(lookup?.isTotalCapped) });
        setHighlighted(-1);
      })
      .catch(() => {
        if (latestRequest.current !== requestId) return;

        setFailed(true);
        setRows([]);
        setCounts([]);
      });
  }, [trimmedTerm, searchable, showCompanyTabs, runLookup]);

  useEffect(() => {
    if (scope !== ALL_RELYING_PARTIES || !searchable) return;
    if (allRowsTerm.current === trimmedTerm) return;

    const requestId = latestAllRequest.current + 1;
    latestAllRequest.current = requestId;
    setAllLoading(true);

    runAllCompanies({ variables: { term: trimmedTerm, limit: RESULT_LIMIT, scope: ALL_RELYING_PARTIES, includeCounts: false } })
      .then(({ data, error }) => {
        if (latestAllRequest.current !== requestId) return;

        setAllLoading(false);
        if (lookupFailed(error)) {
          setFailed(true);
          setAllRows([]);
          return;
        }

        allRowsTerm.current = trimmedTerm;
        setAllRows(data?.quickAccountLookup?.results ?? []);
        setHighlighted(-1);
      })
      .catch(() => {
        if (latestAllRequest.current !== requestId) return;

        setAllLoading(false);
        setFailed(true);
        setAllRows([]);
      });
  }, [scope, trimmedTerm, searchable, runAllCompanies]);

  const isAllCompanies = scope === ALL_RELYING_PARTIES;
  const visibleRows = isAllCompanies ? allRows : rows;
  const busy = isAllCompanies ? allLoading : loading;

  const groups = useMemo(() => groupResults(visibleRows), [visibleRows]);
  const ordered = useMemo(() => groups.flatMap(group => group.rows), [groups]);

  const handoff = useMemo(() => lookupHandoffFor(ordered, term.trim()), [ordered, term]);

  const currentCount = useMemo(() => counts.find(entry => entry.isCurrent), [counts]);

  const otherCompanies = useMemo(() => counts.filter(entry => !entry.isCurrent && entry.count > 0), [counts]);
  const visibleChips = otherCompanies.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenChipCount = otherCompanies.length - visibleChips.length;

  const close = useCallback(() => {
    setAnchorEl(null);
    setHighlighted(-1);
  }, []);

  const dismiss = useCallback(() => {
    close();
    setTerm('');
    setScope(CURRENT_RELYING_PARTY);
  }, [close]);

  const choose = useCallback(
    (row: QuickPatientSearchResult) => {
      dismiss();
      onSelectPatient(row);
    },
    [dismiss, onSelectPatient]
  );

  const seeAll = useCallback(
    (handoff: LookupHandoff) => {
      dismiss();
      onSeeAllResults(handoff, scope);
    },
    [dismiss, onSeeAllResults, scope]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismiss();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (ordered.length === 0) return;

        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;

        setHighlighted(current => {
          if (current === -1) return step === 1 ? 0 : ordered.length - 1;

          return (current + step + ordered.length) % ordered.length;
        });
        return;
      }

      if (event.key !== 'Enter') return;

      event.preventDefault();
      const selected = ordered[highlighted];

      if (selected) {
        choose(selected);
        return;
      }

      // A question wins. Otherwise Enter does what "See all results" does, which is nothing
      // when the lookup page has no field for what was matched.
      if (isPrompt && onSubmitTerm) {
        const question = term;
        dismiss();
        onSubmitTerm(question);
        return;
      }

      if (handoff) seeAll(handoff);
    },
    [choose, dismiss, handoff, highlighted, isPrompt, onSubmitTerm, ordered, seeAll, term]
  );

  // Recent questions are worth offering at the two moments a patient list cannot be: before
  // anything is typed, and once the text is too long to be a name.
  const showPrompts = savedPrompts.length > 0 && Boolean(onSelectPrompt) && (isPrompt || term.trim().length === 0);
  // Ideas only fill the empty box: once something is typed, the results are the point.
  const showIdeas = ideaGroups.length > 0 && Boolean(onSelectPrompt) && term.trim().length === 0;
  const open = Boolean(anchorEl) && (term.trim().length > 0 || showPrompts || showIdeas);
  const showTabs = showCompanyTabs && searchable && !failed && counts.length > 0;

  const renderStatus = () => {
    // With recent questions listed below, the empty box needs no instruction of its own.
    if ((showPrompts || showIdeas) && term.trim().length === 0) return null;
    // Says what Enter will do, rather than implying more typing would find a patient.
    if (isPrompt) return <Small color="text.secondary">Press Enter to ask this as a question.</Small>;
    if (!searchable) return <Small color="text.secondary">Keep typing to search patients…</Small>;
    if (busy && visibleRows.length === 0) {
      return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <CircularProgress size={14} />
          <Small color="text.secondary">Searching…</Small>
        </Stack>
      );
    }
    // A failed search must never read as "no such patient" - that is a wrong answer, not a
    // worse one.
    if (failed) return <Small color="error.main">Search is unavailable right now.</Small>;
    if (visibleRows.length === 0) {
      return (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Small color="text.secondary">No patients matched.</Small>
          <Tooltip title={SEARCH_HELP} placement="top" arrow slotProps={{ tooltip: { sx: { maxWidth: 440 } } }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        </Stack>
      );
    }

    return null;
  };

  const status = renderStatus();

  const renderTabs = () => (
    <Stack
      direction="row"
      useFlexGap
      spacing={0.75}
      sx={{ flexWrap: 'wrap', alignItems: 'center', px: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}
    >
      <Chip
        size="small"
        clickable
        label={`${currentCount?.name ?? 'This pharmacy'} · ${formatCount(currentCount?.count ?? 0, Boolean(currentCount?.isCapped))}`}
        color={isAllCompanies ? 'default' : 'primary'}
        variant={isAllCompanies ? 'outlined' : 'filled'}
        onClick={() => setScope(CURRENT_RELYING_PARTY)}
      />
      <Chip
        size="small"
        clickable
        label={`All Pharmacies · ${formatCount(total.count, total.isCapped)}`}
        color={isAllCompanies ? 'primary' : 'default'}
        variant={isAllCompanies ? 'filled' : 'outlined'}
        onClick={() => setScope(ALL_RELYING_PARTIES)}
      />

      {visibleChips.map(entry => (
        <Small key={entry.relyingPartyId} color="text.secondary" sx={{ px: 1.25 }}>
          {entry.name} · {formatCount(entry.count, entry.isCapped)}
        </Small>
      ))}
      {hiddenChipCount > 0 && (
        <Small color="text.secondary" sx={{ px: 1.25 }}>
          +{hiddenChipCount} more
        </Small>
      )}
    </Stack>
  );

  return (
    <ClickAwayListener onClickAway={close}>
      <Stack sx={{ flex: 1 }}>
        <TruentityTextField
          value={term}
          onChange={event => {
            setTerm(event.target.value);
            setAnchorEl(event.currentTarget);
          }}
          onFocus={event => {
            // A fresh pick each time the box opens, so the ideas vary.
            setIdeaGroups(getQuestionIdeas?.() ?? []);
            setAnchorEl(event.currentTarget);
          }}
          onKeyDown={onKeyDown}
          icon={<SearchIcon />}
          placeholder={placeholder ?? (canAskQuestions ? 'Search patients, or ask a question' : 'Search patients')}
          autoComplete="off"
        />

        <Popper open={open} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300, width: anchorEl?.clientWidth }}>
          <Paper elevation={4} sx={{ mt: 0.5, py: 1 }}>
            {showTabs && renderTabs()}

            {status && <Stack sx={{ px: 2, py: 1 }}>{status}</Stack>}

            {showIdeas &&
              ideaGroups.map(group => (
                <Stack key={group.heading}>
                  <Small color="text.secondary" sx={{ px: 2, py: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {group.heading}
                  </Small>

                  {group.questions.map(question => (
                    <Stack
                      key={question}
                      direction="row"
                      spacing={1}
                      onClick={() => {
                        close();
                        onSelectPrompt?.(question);
                      }}
                      sx={{ alignItems: 'center', px: 2, py: 0.75, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <AutoAwesomeIcon fontSize="small" color="primary" />
                      <Body2 sx={{ minWidth: 0 }}>{question}</Body2>
                    </Stack>
                  ))}
                </Stack>
              ))}

            {showPrompts && (
              <Stack>
                <Small color="text.secondary" sx={{ px: 2, py: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Recent questions
                </Small>

                {savedPrompts.map(prompt => (
                  <Stack
                    key={prompt}
                    direction="row"
                    spacing={1}
                    onClick={() => {
                      close();
                      setTerm('');
                      onSelectPrompt?.(prompt);
                    }}
                    sx={{ alignItems: 'center', px: 2, py: 0.75, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                  >
                    <HistoryIcon fontSize="small" color="disabled" />
                    <Body2 sx={{ minWidth: 0 }}>{prompt}</Body2>
                  </Stack>
                ))}
              </Stack>
            )}

            {groups.map(group => (
              <Stack key={group.heading}>
                <Small color="text.secondary" sx={{ px: 2, py: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {group.heading}
                </Small>

                {group.rows.map(row => {
                  const index = ordered.indexOf(row);

                  return (
                    <Stack
                      key={rowKey(row)}
                      direction="row"
                      spacing={1}
                      onMouseEnter={() => setHighlighted(index)}
                      onClick={() => choose(row)}
                      sx={{
                        alignItems: 'center',
                        px: 2,
                        py: 0.75,
                        cursor: 'pointer',
                        backgroundColor: index === highlighted ? 'action.hover' : 'transparent'
                      }}
                    >
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Body2 sx={{ fontWeight: 600 }}>{row.name ?? 'Unknown patient'}</Body2>
                        <Small color="text.secondary">{secondaryLine(row)}</Small>
                      </Stack>
                      {row.isFuzzy && <Chip size="small" variant="outlined" label="close match" />}
                      {isAllCompanies && row.relyingPartyName && (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<BusinessIcon />}
                          label={row.relyingPartyName}
                          sx={{ '& .MuiChip-label': { px: 1.25 } }}
                        />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            ))}

            {(handoff || failed) && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Stack
                  onClick={() => seeAll(handoff ?? { filterOption: '', value: term.trim() })}
                  sx={{ px: 2, py: 0.75, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <Body2 color="primary.main">{failed ? 'Open the full patient list' : `See all results for “${term}”`}</Body2>
                </Stack>
              </>
            )}

            {fieldOverrides.length > 0 && searchable && !isAllCompanies && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Small color="text.secondary" sx={{ px: 2, py: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Search one field only
                </Small>
                <Stack direction="row" useFlexGap spacing={0.5} sx={{ flexWrap: 'wrap', px: 1.5, pb: 0.5 }}>
                  {fieldOverrides.map(override => (
                    <Chip
                      key={override.label}
                      size="small"
                      variant="outlined"
                      clickable
                      label={override.label}
                      onClick={() => {
                        const value = term;
                        dismiss();
                        override.onSelect(value);
                      }}
                    />
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </Popper>
      </Stack>
    </ClickAwayListener>
  );
};

export default PatientSearchBox;
